import React from 'react';
import type { SlideBgmMoodId, SlideBgmTrack } from '../../utils/slideBgmPlayback';
import { slideBgmTracksForMood } from '../../utils/slideBgmPlayback';

export type ProgramSlideBgmMoodId = SlideBgmMoodId;

export type ProgramSlideBgmPick =
  | { moodId: ProgramSlideBgmMoodId; moodLabel: string; mode: 'random' }
  | { moodId: ProgramSlideBgmMoodId; moodLabel: string; mode: 'track'; track: SlideBgmTrack };

export type ProgramSlideBgmModalProps = {
  open: boolean;
  onClose: () => void;
  onPick: (pick: ProgramSlideBgmPick) => void;
};

/** 무드 섹션 (리모컨에서도 재사용) */
export const BGM_MOOD_SECTIONS: ReadonlyArray<{ id: ProgramSlideBgmMoodId; label: string }> = [
  { id: 'calm', label: '차분,감성' },
  { id: 'cheerful', label: '유쾌,활발' },
  { id: 'tense', label: '긴장,스릴' },
  { id: 'melancholy', label: '서정,여운' },
];

export const BGM_MODAL_CACHE_HINT = '트랙은 기기에서 한 번 받은 뒤 다시 재생할 때 네트워크를 쓰지 않습니다.';

const ProgramSlideBgmModal: React.FC<ProgramSlideBgmModalProps> = ({ open, onClose, onPick }) => {
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="cp-program-slide-timer-modal-backdrop"
      role="presentation"
      data-tutorial-pres="bgm-modal"
      onClick={onClose}
    >
      <div
        className="cp-program-slide-timer-modal cp-program-slide-bgm-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-slide-bgm-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cp-program-slide-timer-modal-header">
          <h2 id="cp-slide-bgm-modal-title" className="cp-program-slide-timer-modal-title">
            BGM
          </h2>
          <button type="button" className="cp-program-slide-timer-modal-close" onClick={onClose} aria-label="닫기">
            <svg
              className="cp-program-slide-timer-modal-close-icon"
              viewBox="0 0 24 24"
              width="20"
              height="20"
              aria-hidden
            >
              <path
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                d="M6 6l12 12M18 6L6 18"
              />
            </svg>
          </button>
        </div>
        <p className="cp-program-slide-bgm-modal-hint">
          {BGM_MODAL_CACHE_HINT}
        </p>
        <div className="cp-program-slide-bgm-modal-body">
          {BGM_MOOD_SECTIONS.map((section) => (
            <section key={section.id} className="cp-program-slide-bgm-category" aria-labelledby={`cp-bgm-cat-${section.id}`}>
              <h3 id={`cp-bgm-cat-${section.id}`} className="cp-program-slide-bgm-category-title">
                {section.label}
              </h3>
              <div className="cp-program-slide-bgm-category-actions" role="group" aria-label={`${section.label} 트랙`}>
                <button
                  type="button"
                  className="cp-program-slide-timer-preset"
                  data-tutorial-slide="bgm-pick"
                  onClick={() => onPick({ moodId: section.id, moodLabel: section.label, mode: 'random' })}
                >
                  랜덤 플레이
                </button>
                {slideBgmTracksForMood(section.id).map((track) => (
                  <button
                    key={track}
                    type="button"
                    className="cp-program-slide-timer-preset"
                    data-tutorial-slide="bgm-pick"
                    onClick={() =>
                      onPick({ moodId: section.id, moodLabel: section.label, mode: 'track', track })
                    }
                  >
                    {section.label} {track}
                  </button>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProgramSlideBgmModal;
